import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';
import { formatAppDate } from '../../utils/localeDateFormat';
import type { AppLocale } from '../../i18n';

export interface DailyActivityHeatmapCell {
  date: string;
  count: number;
  level: number;
}

export interface UserActivityHeatmapData {
  startDate: string;
  endDate: string;
  timeZoneId: string;
  totalActions: number;
  activeDays: number;
  maxDailyCount: number;
  days: DailyActivityHeatmapCell[];
}

interface ActivityHeatmapProps {
  data: UserActivityHeatmapData | null | undefined;
  loading?: boolean;
  className?: string;
  compact?: boolean;
  title?: string;
  subtitle?: string;
}

interface HeatmapTooltipState {
  date: string;
  count: number;
  x: number;
  y: number;
}

function parseDateOnly(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatHeatmapDate(value: string, locale: AppLocale): string {
  return formatAppDate(parseDateOnly(value), locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMonthLabel(date: Date, locale: AppLocale): string {
  return formatAppDate(date, locale, { month: 'short' });
}

function formatDayLabel(dayIndex: number, locale: AppLocale): string {
  const ref = new Date(2024, 0, 7 + dayIndex);
  return formatAppDate(ref, locale, { weekday: 'short' });
}

/** GitHub-style levels relative to the busiest day in the range. */
function computeRelativeLevel(count: number, maxDailyCount: number): number {
  if (count <= 0) return 0;
  if (maxDailyCount <= 0) return 1;
  const ratio = count / maxDailyCount;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
}

function getLevelColor(level: number, isDark: boolean): string {
  if (isDark) {
    switch (level) {
      case 0: return '#161b22';
      case 1: return '#0e4429';
      case 2: return '#006d32';
      case 3: return '#26a641';
      case 4: return '#39d353';
      default: return '#161b22';
    }
  }
  switch (level) {
    case 0: return '#ebedf0';
    case 1: return '#9be9a8';
    case 2: return '#40c463';
    case 3: return '#30a14e';
    case 4: return '#216e39';
    default: return '#ebedf0';
  }
}

interface WeekColumn {
  weekIndex: number;
  cells: Array<{ date: string; count: number; level: number; dayOfWeek: number } | null>;
  monthLabel: string | null;
}

function HeatmapTooltip({
  tooltip,
  isDark,
  formatDate,
  actionsText,
}: {
  tooltip: HeatmapTooltipState;
  isDark: boolean;
  formatDate: (value: string) => string;
  actionsText: string;
}) {
  return createPortal(
    <div
      className="pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-[calc(100%+8px)] rounded-md px-2.5 py-1.5 text-xs shadow-lg border whitespace-nowrap"
      style={{
        left: tooltip.x,
        top: tooltip.y,
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        borderColor: isDark ? '#374151' : '#e5e7eb',
        color: isDark ? '#f3f4f6' : '#111827',
      }}
      role="tooltip"
    >
      <span className="font-medium">{formatDate(tooltip.date)}</span>
      <span className="mx-1.5 text-gray-400">·</span>
      <span>{actionsText}</span>
    </div>,
    document.body
  );
}

export const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({
  data,
  loading = false,
  className = '',
  compact = false,
  title,
  subtitle,
}) => {
  const { isDark } = useTheme();
  const { locale } = useLocale();
  const { t } = useTranslation('profile');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<HeatmapTooltipState | null>(null);

  const formatDate = useCallback((value: string) => formatHeatmapDate(value, locale), [locale]);

  const actionsLabel = useCallback(
    (count: number) => t(count === 1 ? 'heatmap.actionsCount' : 'heatmap.actionsCount_plural', { count }),
    [t]
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  useEffect(() => {
    if (!tooltip) return;
    const scrollEl = scrollRef.current;
    const onScroll = () => hideTooltip();
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target;
      if (target instanceof Element && target.closest('.heatmap-cell')) return;
      hideTooltip();
    };
    scrollEl?.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      scrollEl?.removeEventListener('scroll', onScroll);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [tooltip, hideTooltip]);

  const showTooltipForCell = useCallback(
    (cell: { date: string; count: number }, target: HTMLElement) => {
      const rect = target.getBoundingClientRect();
      setTooltip({
        date: cell.date,
        count: cell.count,
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
    },
    []
  );

  const { weeks, legendLevels, summary, maxDailyCount } = useMemo(() => {
    if (!data?.days?.length) {
      return { weeks: [] as WeekColumn[], legendLevels: [0, 1, 2, 3, 4], summary: null, maxDailyCount: 0 };
    }

    const countMap = new Map(data.days.map((d) => [d.date, d]));
    const maxDaily =
      data.maxDailyCount > 0
        ? data.maxDailyCount
        : Math.max(0, ...data.days.map((d) => d.count));
    const start = parseDateOnly(data.startDate);
    const end = parseDateOnly(data.endDate);

    const gridStart = new Date(start);
    gridStart.setDate(gridStart.getDate() - gridStart.getDay());

    const columns: WeekColumn[] = [];
    const cursor = new Date(gridStart);
    let weekIndex = 0;

    while (cursor <= end || cursor.getDay() !== 0) {
      const cells: WeekColumn['cells'] = [];
      let monthLabel: string | null = null;

      for (let dow = 0; dow < 7; dow++) {
        const dateStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
        const inRange = cursor >= start && cursor <= end;
        if (inRange && dow === 0 && (cursor.getDate() <= 7 || weekIndex === 0)) {
          monthLabel = formatMonthLabel(cursor, locale);
        }
        if (inRange) {
          const cell = countMap.get(dateStr);
          const count = cell?.count ?? 0;
          cells.push({
            date: dateStr,
            count,
            level: computeRelativeLevel(count, maxDaily),
            dayOfWeek: dow,
          });
        } else {
          cells.push(null);
        }
        cursor.setDate(cursor.getDate() + 1);
      }

      columns.push({ weekIndex, cells, monthLabel });
      weekIndex++;
      if (cursor > end && cursor.getDay() === 0) break;
      if (weekIndex > 60) break;
    }

    return {
      weeks: columns,
      legendLevels: [0, 1, 2, 3, 4],
      maxDailyCount: maxDaily,
      summary: {
        totalActions: data.totalActions,
        activeDays: data.activeDays,
        timeZoneId: data.timeZoneId,
      },
    };
  }, [data, locale]);

  const dayLabels = useMemo(
    () => [0, 1, 2, 3, 4, 5, 6].map((i) => formatDayLabel(i, locale)),
    [locale]
  );

  const cellSize = compact ? 11 : 13;
  const cellGap = compact ? 2 : 3;

  const resolvedTitle = title ?? t('progress.charts.activityOverTime');

  if (loading) {
    return (
      <div className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-6 ${className}`}>
        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
          <span className="inline-flex h-5 w-5 border-2 border-gray-200 border-t-emerald-500 rounded-full animate-spin" />
          {t('heatmap.loading')}
        </div>
      </div>
    );
  }

  if (!data?.days?.length) {
    return (
      <div className={`rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/30 p-6 text-center ${className}`}>
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('heatmap.noData')}</p>
      </div>
    );
  }

  const defaultSubtitle =
    summary && maxDailyCount > 0
      ? t('heatmap.summarySubtitle', {
          totalActions: summary.totalActions.toLocaleString(locale === 'fa' ? 'fa-IR' : 'en-US'),
          activeDays: summary.activeDays,
          timeZoneId: summary.timeZoneId,
          maxDailyCount: maxDailyCount.toLocaleString(locale === 'fa' ? 'fa-IR' : 'en-US'),
        })
      : null;

  return (
    <div className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4 sm:p-5 ${locale === 'fa' ? 'font-persian' : ''} ${className}`}>
      {tooltip && (
        <HeatmapTooltip
          tooltip={tooltip}
          isDark={isDark}
          formatDate={formatDate}
          actionsText={actionsLabel(tooltip.count)}
        />
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{resolvedTitle}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {subtitle ?? defaultSubtitle}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
          <span>{t('heatmap.less')}</span>
          {legendLevels.map((level) => (
            <span
              key={level}
              className="rounded-sm shrink-0 heatmap-legend-swatch"
              data-level={level}
              style={{ width: cellSize, height: cellSize }}
            />
          ))}
          <span>{t('heatmap.more')}</span>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="activity-heatmap-scroll overflow-x-auto overscroll-x-contain pb-2 -mx-1 px-1"
        style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin', touchAction: 'auto' }}
        onMouseLeave={hideTooltip}
      >
        <div className="inline-flex flex-col min-w-max">
          <div className="flex pl-8 mb-1 heatmap-row-gap" data-gap={cellGap}>
            {weeks.map((week) => (
              <div
                key={`month-${week.weekIndex}`}
                className="text-[10px] text-gray-500 dark:text-gray-400 shrink-0 heatmap-month-col"
                style={{ width: cellSize, minWidth: cellSize }}
              >
                {week.monthLabel ? week.monthLabel.charAt(0) : ''}
              </div>
            ))}
          </div>

          <div className="flex gap-0">
            <div className="flex flex-col pr-2 shrink-0 heatmap-col-gap" data-gap={cellGap}>
              {dayLabels.map((label, i) => (
                <div
                  key={label + i}
                  className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center heatmap-day-label"
                  style={{ height: cellSize, lineHeight: `${cellSize}px` }}
                >
                  {i % 2 === 1 ? label.charAt(0) : ''}
                </div>
              ))}
            </div>

            <div className="flex heatmap-col-gap" data-gap={cellGap}>
              {weeks.map((week) => (
                <div key={`week-${week.weekIndex}`} className="flex flex-col heatmap-col-gap" data-gap={cellGap}>
                  {week.cells.map((cell, rowIdx) =>
                    cell ? (
                      <div
                        key={cell.date}
                        role="gridcell"
                        tabIndex={0}
                        className="heatmap-cell rounded-sm transition-transform hover:scale-110 focus:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/70"
                        style={{
                          width: cellSize,
                          height: cellSize,
                          backgroundColor: getLevelColor(cell.level, isDark),
                          touchAction: 'auto',
                        }}
                        aria-label={t('heatmap.cellAriaLabel', {
                          date: formatDate(cell.date),
                          count: cell.count,
                        })}
                        onMouseEnter={(e) => showTooltipForCell(cell, e.currentTarget)}
                        onMouseMove={(e) => showTooltipForCell(cell, e.currentTarget)}
                        onMouseDown={(e) => e.preventDefault()}
                        onFocus={(e) => {
                          e.currentTarget.focus({ preventScroll: true });
                          showTooltipForCell(cell, e.currentTarget);
                        }}
                        onBlur={hideTooltip}
                        onClick={(e) => showTooltipForCell(cell, e.currentTarget)}
                      />
                    ) : (
                      <div
                        key={`empty-${week.weekIndex}-${rowIdx}`}
                        className="heatmap-cell-spacer"
                        style={{ width: cellSize, height: cellSize }}
                      />
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .heatmap-row-gap, .heatmap-col-gap { gap: ${cellGap}px; }
        .heatmap-legend-swatch[data-level="0"] { background: ${getLevelColor(0, isDark)}; }
        .heatmap-legend-swatch[data-level="1"] { background: ${getLevelColor(1, isDark)}; }
        .heatmap-legend-swatch[data-level="2"] { background: ${getLevelColor(2, isDark)}; }
        .heatmap-legend-swatch[data-level="3"] { background: ${getLevelColor(3, isDark)}; }
        .heatmap-legend-swatch[data-level="4"] { background: ${getLevelColor(4, isDark)}; }
      `}</style>
    </div>
  );
};

export function normalizeHeatmapResponse(raw: unknown): UserActivityHeatmapData | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const daysRaw = (r.days ?? r.Days) as unknown[];
  if (!Array.isArray(daysRaw)) return null;

  return {
    startDate: String(r.startDate ?? r.StartDate ?? ''),
    endDate: String(r.endDate ?? r.EndDate ?? ''),
    timeZoneId: String(r.timeZoneId ?? r.TimeZoneId ?? 'UTC'),
    totalActions: Number(r.totalActions ?? r.TotalActions ?? 0),
    activeDays: Number(r.activeDays ?? r.ActiveDays ?? 0),
    maxDailyCount: Number(r.maxDailyCount ?? r.MaxDailyCount ?? 0),
    days: daysRaw.map((d) => {
      const cell = d as Record<string, unknown>;
      return {
        date: String(cell.date ?? cell.Date ?? ''),
        count: Number(cell.count ?? cell.Count ?? 0),
        level: Number(cell.level ?? cell.Level ?? 0),
      };
    }),
  };
}
